import { APP_TEXT_SIZES, APP_FONT_FAMILIES } from "../enums";


type SizeConfig = {
  key: keyof typeof APP_TEXT_SIZES;
  lineHeight: number | string;
  letterSpacing?: string;
  headingLineHeight?: string; 
};

const SIZE_CONFIGS: SizeConfig[] = [
  { key: "xxsmall", lineHeight: 1.4 },
  { key: "xsmall", lineHeight: 1.4 },
  { key: "small", lineHeight: 1.5 },
  { key: "body", lineHeight: "20px" },
  { key: "bodyLarge", lineHeight: "24px" },
  { key: "standard", lineHeight: 1.5 },
  { key: "medium", lineHeight: 1.5 },
  { key: "large", lineHeight: 1.5 },
  { key: "xlarge", lineHeight: 1.4 },
  { key: "xxlarge", lineHeight: 1.3 },
  { key: "xxxlarge", lineHeight: 1.2 },
  {
    key: "heading6",
    lineHeight: 1.4,
    headingLineHeight: "24px",
  },
  {
    key: "heading5",
    lineHeight: 1.3,
    headingLineHeight: "28px",
  },
  {
    key: "heading4",
    lineHeight: 1.3,
    headingLineHeight: "28px",
  },
  {
    key: "heading3",
    lineHeight: 1.3,
    headingLineHeight: "32px",
  },
  {
    key: "heading2",
    lineHeight: 1.2,
    headingLineHeight: "36px",
  },
  {
    key: "heading1",
    lineHeight: 1.2,
    headingLineHeight: "42px",
  },
  { key: "display", lineHeight: 1.2 },
  { key: "displayLarge", lineHeight: 1.1 },
];


const FONT_FAMILIES = {
  default: { name: null, suffix: "" },
  mono: { name: APP_FONT_FAMILIES.ibmPlexMono, suffix: "-mono" },
  readex: { name: APP_FONT_FAMILIES.readexPro, suffix: "-readex" },
  helveticaNeue: { name: APP_FONT_FAMILIES.helveticaNeue, suffix: "-helveticaNeue" },
  manrope: { name: APP_FONT_FAMILIES.manrope, suffix: "-manrope" },
  ibmPlexMono: { name: APP_FONT_FAMILIES.ibmPlexMono, suffix: "-ibmPlexMono" },
  readexPro: { name: APP_FONT_FAMILIES.readexPro, suffix: "-readexPro" },
} as const;


const WEIGHTS = {
  regular: { value: 400, suffix: "" },
  medium: { value: 500, suffix: "-medium" },
  bold: { value: 600, suffix: "-bold" },
} as const;


const getVariantName = (
  sizeKey: string,
  weight: keyof typeof WEIGHTS,
  fontFamily: keyof typeof FONT_FAMILIES
): string => {
  const fontSuffix = FONT_FAMILIES[fontFamily].suffix;
  const weightSuffix = WEIGHTS[weight].suffix;
  return `${sizeKey}${fontSuffix}${weightSuffix}`;
};

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    
    xxsmall: true;
    xsmall: true;
    small: true;
    body: true;
    bodyLarge: true;
    standard: true;
    medium: true;
    large: true;
    xlarge: true;
    xxlarge: true;
    xxxlarge: true;
    heading6: true;
    heading5: true;
    heading4: true;
    heading3: true;
    heading2: true;
    heading1: true;
    display: true;
    displayLarge: true;
    regular: true;
    "xxsmall-medium": true;
    "xsmall-medium": true;
    "small-medium": true;
    "body-medium": true;
    "bodyLarge-medium": true;
    "standard-medium": true;
    "medium-medium": true;
    "large-medium": true;
    "xlarge-medium": true;
    "xxlarge-medium": true;
    "xxxlarge-medium": true;
    "heading6-medium": true;
    "heading5-medium": true;
    "heading4-medium": true;
    "heading3-medium": true;
    "heading2-medium": true;
    "heading1-medium": true;
    "display-medium": true;
    "displayLarge-medium": true;
    "xxsmall-bold": true;
    "xsmall-bold": true;
    "small-bold": true;
    "body-bold": true;
    "bodyLarge-bold": true;
    "standard-bold": true;
    "medium-bold": true;
    "large-bold": true;
    "xlarge-bold": true;
    "xxlarge-bold": true;
    "xxxlarge-bold": true;
    "heading6-bold": true;
    "heading5-bold": true;
    "heading4-bold": true;
    "heading3-bold": true;
    "heading2-bold": true;
    "heading1-bold": true;
    "display-bold": true;
    "displayLarge-bold": true;
    
    "xxsmall-mono": true;
    "xsmall-mono": true;
    "small-mono": true;
    "body-mono": true;
    "bodyLarge-mono": true;
    "standard-mono": true;
    "medium-mono": true;
    "large-mono": true;
    "xlarge-mono": true;
    "xxlarge-mono": true;
    "xxxlarge-mono": true;
    "heading6-mono": true;
    "heading5-mono": true;
    "heading4-mono": true;
    "heading3-mono": true;
    "heading2-mono": true;
    "heading1-mono": true;
    "display-mono": true;
    "displayLarge-mono": true;
    "xxsmall-mono-medium": true;
    "xxsmall-mono-bold": true;
    "xsmall-mono-medium": true;
    "xsmall-mono-bold": true;
    "small-mono-medium": true;
    "small-mono-bold": true;
    "body-mono-medium": true;
    "body-mono-bold": true;
    "bodyLarge-mono-medium": true;
    "bodyLarge-mono-bold": true;
    "standard-mono-medium": true;
    "standard-mono-bold": true;
    "medium-mono-medium": true;
    "medium-mono-bold": true;
    "large-mono-medium": true;
    "large-mono-bold": true;
    "xlarge-mono-medium": true;
    "xlarge-mono-bold": true;
    "xxlarge-mono-medium": true;
    "xxlarge-mono-bold": true;
    "xxxlarge-mono-medium": true;
    "xxxlarge-mono-bold": true;
    "heading6-mono-medium": true;
    "heading6-mono-bold": true;
    "heading5-mono-medium": true;
    "heading5-mono-bold": true;
    "heading4-mono-medium": true;
    "heading4-mono-bold": true;
    "heading3-mono-medium": true;
    "heading3-mono-bold": true;
    "heading2-mono-medium": true;
    "heading2-mono-bold": true;
    "heading1-mono-medium": true;
    "heading1-mono-bold": true;
    "display-mono-medium": true;
    "display-mono-bold": true;
    "displayLarge-mono-medium": true;
    "displayLarge-mono-bold": true;
    
    "xxsmall-readex": true;
    "xsmall-readex": true;
    "small-readex": true;
    "body-readex": true;
    "bodyLarge-readex": true;
    "standard-readex": true;
    "medium-readex": true;
    "large-readex": true;
    "xlarge-readex": true;
    "xxlarge-readex": true;
    "xxxlarge-readex": true;
    "heading6-readex": true;
    "heading5-readex": true;
    "heading4-readex": true;
    "heading3-readex": true;
    "heading2-readex": true;
    "heading1-readex": true;
    "display-readex": true;
    "displayLarge-readex": true;
    "xxsmall-readex-medium": true;
    "xxsmall-readex-bold": true;
    "xsmall-readex-medium": true;
    "xsmall-readex-bold": true;
    "small-readex-medium": true;
    "small-readex-bold": true;
    "body-readex-medium": true;
    "body-readex-bold": true;
    "bodyLarge-readex-medium": true;
    "bodyLarge-readex-bold": true;
    "standard-readex-medium": true;
    "standard-readex-bold": true;
    "medium-readex-medium": true;
    "medium-readex-bold": true;
    "large-readex-medium": true;
    "large-readex-bold": true;
    "xlarge-readex-medium": true;
    "xlarge-readex-bold": true;
    "xxlarge-readex-medium": true;
    "xxlarge-readex-bold": true;
    "xxxlarge-readex-medium": true;
    "xxxlarge-readex-bold": true;
    "heading6-readex-medium": true;
    "heading6-readex-bold": true;
    "heading5-readex-medium": true;
    "heading5-readex-bold": true;
    "heading4-readex-medium": true;
    "heading4-readex-bold": true;
    "heading3-readex-medium": true;
    "heading3-readex-bold": true;
    "heading2-readex-medium": true;
    "heading2-readex-bold": true;
    "heading1-readex-medium": true;
    "heading1-readex-bold": true;
    "display-readex-medium": true;
    "display-readex-bold": true;
    "displayLarge-readex-medium": true;
    "displayLarge-readex-bold": true;
    
    "xxsmall-helveticaNeue": true;
    "xsmall-helveticaNeue": true;
    "small-helveticaNeue": true;
    "body-helveticaNeue": true;
    "bodyLarge-helveticaNeue": true;
    "standard-helveticaNeue": true;
    "medium-helveticaNeue": true;
    "large-helveticaNeue": true;
    "xlarge-helveticaNeue": true;
    "xxlarge-helveticaNeue": true;
    "xxxlarge-helveticaNeue": true;
    "heading6-helveticaNeue": true;
    "heading5-helveticaNeue": true;
    "heading4-helveticaNeue": true;
    "heading3-helveticaNeue": true;
    "heading2-helveticaNeue": true;
    "heading1-helveticaNeue": true;
    "display-helveticaNeue": true;
    "displayLarge-helveticaNeue": true;
    "xxsmall-helveticaNeue-medium": true;
    "xxsmall-helveticaNeue-bold": true;
    "xsmall-helveticaNeue-medium": true;
    "xsmall-helveticaNeue-bold": true;
    "small-helveticaNeue-medium": true;
    "small-helveticaNeue-bold": true;
    "body-helveticaNeue-medium": true;
    "body-helveticaNeue-bold": true;
    "bodyLarge-helveticaNeue-medium": true;
    "bodyLarge-helveticaNeue-bold": true;
    "standard-helveticaNeue-medium": true;
    "standard-helveticaNeue-bold": true;
    "medium-helveticaNeue-medium": true;
    "medium-helveticaNeue-bold": true;
    "large-helveticaNeue-medium": true;
    "large-helveticaNeue-bold": true;
    "xlarge-helveticaNeue-medium": true;
    "xlarge-helveticaNeue-bold": true;
    "xxlarge-helveticaNeue-medium": true;
    "xxlarge-helveticaNeue-bold": true;
    "xxxlarge-helveticaNeue-medium": true;
    "xxxlarge-helveticaNeue-bold": true;
    "heading6-helveticaNeue-medium": true;
    "heading6-helveticaNeue-bold": true;
    "heading5-helveticaNeue-medium": true;
    "heading5-helveticaNeue-bold": true;
    "heading4-helveticaNeue-medium": true;
    "heading4-helveticaNeue-bold": true;
    "heading3-helveticaNeue-medium": true;
    "heading3-helveticaNeue-bold": true;
    "heading2-helveticaNeue-medium": true;
    "heading2-helveticaNeue-bold": true;
    "heading1-helveticaNeue-medium": true;
    "heading1-helveticaNeue-bold": true;
    "display-helveticaNeue-medium": true;
    "display-helveticaNeue-bold": true;
    "displayLarge-helveticaNeue-medium": true;
    "displayLarge-helveticaNeue-bold": true;
    
    "xxsmall-manrope": true;
    "xsmall-manrope": true;
    "small-manrope": true;
    "body-manrope": true;
    "bodyLarge-manrope": true;
    "standard-manrope": true;
    "medium-manrope": true;
    "large-manrope": true;
    "xlarge-manrope": true;
    "xxlarge-manrope": true;
    "xxxlarge-manrope": true;
    "heading6-manrope": true;
    "heading5-manrope": true;
    "heading4-manrope": true;
    "heading3-manrope": true;
    "heading2-manrope": true;
    "heading1-manrope": true;
    "display-manrope": true;
    "displayLarge-manrope": true;
    "xxsmall-manrope-medium": true;
    "xxsmall-manrope-bold": true;
    "xsmall-manrope-medium": true;
    "xsmall-manrope-bold": true;
    "small-manrope-medium": true;
    "small-manrope-bold": true;
    "body-manrope-medium": true;
    "body-manrope-bold": true;
    "bodyLarge-manrope-medium": true;
    "bodyLarge-manrope-bold": true;
    "standard-manrope-medium": true;
    "standard-manrope-bold": true;
    "medium-manrope-medium": true;
    "medium-manrope-bold": true;
    "large-manrope-medium": true;
    "large-manrope-bold": true;
    "xlarge-manrope-medium": true;
    "xlarge-manrope-bold": true;
    "xxlarge-manrope-medium": true;
    "xxlarge-manrope-bold": true;
    "xxxlarge-manrope-medium": true;
    "xxxlarge-manrope-bold": true;
    "heading6-manrope-medium": true;
    "heading6-manrope-bold": true;
    "heading5-manrope-medium": true;
    "heading5-manrope-bold": true;
    "heading4-manrope-medium": true;
    "heading4-manrope-bold": true;
    "heading3-manrope-medium": true;
    "heading3-manrope-bold": true;
    "heading2-manrope-medium": true;
    "heading2-manrope-bold": true;
    "heading1-manrope-medium": true;
    "heading1-manrope-bold": true;
    "display-manrope-medium": true;
    "display-manrope-bold": true;
    "displayLarge-manrope-medium": true;
    "displayLarge-manrope-bold": true;
    
    "xxsmall-ibmPlexMono": true;
    "xsmall-ibmPlexMono": true;
    "small-ibmPlexMono": true;
    "body-ibmPlexMono": true;
    "bodyLarge-ibmPlexMono": true;
    "standard-ibmPlexMono": true;
    "medium-ibmPlexMono": true;
    "large-ibmPlexMono": true;
    "xlarge-ibmPlexMono": true;
    "xxlarge-ibmPlexMono": true;
    "xxxlarge-ibmPlexMono": true;
    "heading6-ibmPlexMono": true;
    "heading5-ibmPlexMono": true;
    "heading4-ibmPlexMono": true;
    "heading3-ibmPlexMono": true;
    "heading2-ibmPlexMono": true;
    "heading1-ibmPlexMono": true;
    "display-ibmPlexMono": true;
    "displayLarge-ibmPlexMono": true;
    "xxsmall-ibmPlexMono-medium": true;
    "xxsmall-ibmPlexMono-bold": true;
    "xsmall-ibmPlexMono-medium": true;
    "xsmall-ibmPlexMono-bold": true;
    "small-ibmPlexMono-medium": true;
    "small-ibmPlexMono-bold": true;
    "body-ibmPlexMono-medium": true;
    "body-ibmPlexMono-bold": true;
    "bodyLarge-ibmPlexMono-medium": true;
    "bodyLarge-ibmPlexMono-bold": true;
    "standard-ibmPlexMono-medium": true;
    "standard-ibmPlexMono-bold": true;
    "medium-ibmPlexMono-medium": true;
    "medium-ibmPlexMono-bold": true;
    "large-ibmPlexMono-medium": true;
    "large-ibmPlexMono-bold": true;
    "xlarge-ibmPlexMono-medium": true;
    "xlarge-ibmPlexMono-bold": true;
    "xxlarge-ibmPlexMono-medium": true;
    "xxlarge-ibmPlexMono-bold": true;
    "xxxlarge-ibmPlexMono-medium": true;
    "xxxlarge-ibmPlexMono-bold": true;
    "heading6-ibmPlexMono-medium": true;
    "heading6-ibmPlexMono-bold": true;
    "heading5-ibmPlexMono-medium": true;
    "heading5-ibmPlexMono-bold": true;
    "heading4-ibmPlexMono-medium": true;
    "heading4-ibmPlexMono-bold": true;
    "heading3-ibmPlexMono-medium": true;
    "heading3-ibmPlexMono-bold": true;
    "heading2-ibmPlexMono-medium": true;
    "heading2-ibmPlexMono-bold": true;
    "heading1-ibmPlexMono-medium": true;
    "heading1-ibmPlexMono-bold": true;
    "display-ibmPlexMono-medium": true;
    "display-ibmPlexMono-bold": true;
    "displayLarge-ibmPlexMono-medium": true;
    "displayLarge-ibmPlexMono-bold": true;
    
    "xxsmall-readexPro": true;
    "xsmall-readexPro": true;
    "small-readexPro": true;
    "body-readexPro": true;
    "bodyLarge-readexPro": true;
    "standard-readexPro": true;
    "medium-readexPro": true;
    "large-readexPro": true;
    "xlarge-readexPro": true;
    "xxlarge-readexPro": true;
    "xxxlarge-readexPro": true;
    "heading6-readexPro": true;
    "heading5-readexPro": true;
    "heading4-readexPro": true;
    "heading3-readexPro": true;
    "heading2-readexPro": true;
    "heading1-readexPro": true;
    "display-readexPro": true;
    "displayLarge-readexPro": true;
    "xxsmall-readexPro-medium": true;
    "xxsmall-readexPro-bold": true;
    "xsmall-readexPro-medium": true;
    "xsmall-readexPro-bold": true;
    "small-readexPro-medium": true;
    "small-readexPro-bold": true;
    "body-readexPro-medium": true;
    "body-readexPro-bold": true;
    "bodyLarge-readexPro-medium": true;
    "bodyLarge-readexPro-bold": true;
    "standard-readexPro-medium": true;
    "standard-readexPro-bold": true;
    "medium-readexPro-medium": true;
    "medium-readexPro-bold": true;
    "large-readexPro-medium": true;
    "large-readexPro-bold": true;
    "xlarge-readexPro-medium": true;
    "xlarge-readexPro-bold": true;
    "xxlarge-readexPro-medium": true;
    "xxlarge-readexPro-bold": true;
    "xxxlarge-readexPro-medium": true;
    "xxxlarge-readexPro-bold": true;
    "heading6-readexPro-medium": true;
    "heading6-readexPro-bold": true;
    "heading5-readexPro-medium": true;
    "heading5-readexPro-bold": true;
    "heading4-readexPro-medium": true;
    "heading4-readexPro-bold": true;
    "heading3-readexPro-medium": true;
    "heading3-readexPro-bold": true;
    "heading2-readexPro-medium": true;
    "heading2-readexPro-bold": true;
    "heading1-readexPro-medium": true;
    "heading1-readexPro-bold": true;
    "display-readexPro-medium": true;
    "display-readexPro-bold": true;
    "displayLarge-readexPro-medium": true;
    "displayLarge-readexPro-bold": true;
  }
}

interface TypographyVariantConfig {
  fontSize: number;
  fontWeight?: number;
  lineHeight?: number | string;
  letterSpacing?: number | string;
  fontFamily?: string;
}

const createTypographyVariant = ({
  fontSize,
  fontWeight = 400,
  lineHeight,
  letterSpacing,
  fontFamily = APP_FONT_FAMILIES.manrope,
}: TypographyVariantConfig) => ({
  fontSize: `${fontSize}px`,
  fontWeight,
  lineHeight: lineHeight || "normal",
  letterSpacing: letterSpacing || "normal",
  fontFamily,
});


const generateVariants = () => {
  const variants: any[] = [];

  
  SIZE_CONFIGS.forEach((config) => {
    const fontSize = APP_TEXT_SIZES[config.key];
    const lineHeight = config.headingLineHeight || config.lineHeight;

    variants.push({
      props: { variant: config.key },
      style: createTypographyVariant({
        fontSize,
        fontWeight: 400,
        lineHeight,
        letterSpacing: config.letterSpacing,
      }),
    });
  });

  
  variants.push({
    props: { variant: "regular" },
    style: createTypographyVariant({
      fontSize: APP_TEXT_SIZES.standard,
      fontWeight: 400,
    }),
  });

  
  SIZE_CONFIGS.forEach((config) => {
    const fontSize = APP_TEXT_SIZES[config.key];
    const lineHeight = config.headingLineHeight || config.lineHeight;

    Object.keys(FONT_FAMILIES).forEach((fontFamilyKey) => {
      if (fontFamilyKey === "default") return; 

      const fontFamily =
        FONT_FAMILIES[fontFamilyKey as keyof typeof FONT_FAMILIES];
      const variantName = `${config.key}${fontFamily.suffix}`;

      variants.push({
        props: { variant: variantName },
        style: createTypographyVariant({
          fontSize,
          fontWeight: 400,
          lineHeight,
          letterSpacing: config.letterSpacing,
          fontFamily: fontFamily.name || undefined,
        }),
      });
    });
  });

  
  SIZE_CONFIGS.forEach((config) => {
    const fontSize = APP_TEXT_SIZES[config.key];
    const isHeading = config.headingLineHeight !== undefined;
    const baseLineHeight = isHeading
      ? config.headingLineHeight!
      : config.lineHeight;

    Object.keys(WEIGHTS).forEach((weightKey) => {
      if (weightKey === "regular") return; 

      const weight = WEIGHTS[weightKey as keyof typeof WEIGHTS];

      Object.keys(FONT_FAMILIES).forEach((fontFamilyKey) => {
        const fontFamily =
          FONT_FAMILIES[fontFamilyKey as keyof typeof FONT_FAMILIES];
        const variantName = getVariantName(
          config.key,
          weightKey as keyof typeof WEIGHTS,
          fontFamilyKey as keyof typeof FONT_FAMILIES
        );

        variants.push({
          props: { variant: variantName },
          style: createTypographyVariant({
            fontSize,
            fontWeight: weight.value,
            lineHeight: baseLineHeight,
            letterSpacing: config.letterSpacing,
            fontFamily: fontFamily.name || undefined,
          }),
        });
      });
    });
  });

  
  variants.push({
    props: { className: "truncated-text" },
    style: {
      overflow: "hidden",
      textOverflow: "ellipsis",
      display: "-webkit-box",
      WebkitLineClamp: "1",
      WebkitBoxOrient: "vertical",
    },
  });

  return variants;
};

const customMuiTypography: any = {
  styleOverrides: {
    root: {
      textTransform: "none",
      fontFamily: APP_FONT_FAMILIES.manrope,

      lineHeight: "24px",
    },
  },
  variants: generateVariants(),
};

export { customMuiTypography };
